import React, { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  CheckCircleIcon,
  XCircleIcon,
  CircleNotchIcon,
  SparkleIcon,
  EnvelopeSimpleIcon,
} from "@phosphor-icons/react";

/**
 * Landing-page widget that lets unauthed visitors soft-reserve a desired
 * @username for 48 hours via email magic-link.
 *
 * Backend endpoints:
 *   GET  /api/username-availability/{username}    – live debounced check
 *   POST /api/username-reservations               – create + email claim link
 *
 * After submission, the visitor sees a success card. On their email they
 * tap a link like /register?reservation=<token> and the Register page
 * prefills email + handle (locked) so they finish signup in one step.
 */
const SANITIZE = (v) => v.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 20);

export default function UsernameReserveCard() {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | checking | available | taken | invalid
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reserved, setReserved] = useState(null); // {username, expires_at}
  const checkSeq = useRef(0);

  // Live availability check — debounced 350ms.
  useEffect(() => {
    if (!username) {
      setStatus("idle");
      setReason("");
      return;
    }
    if (username.length < 3) {
      setStatus("invalid");
      setReason("3 characters minimum");
      return;
    }
    setStatus("checking");
    const seq = ++checkSeq.current;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(
          `/username-availability/${encodeURIComponent(username)}`
        );
        if (seq !== checkSeq.current) return; // stale response
        if (data.available) {
          setStatus("available");
          setReason("");
        } else {
          setStatus("taken");
          setReason(data.reason || "Already taken");
        }
      } catch {
        if (seq !== checkSeq.current) return;
        setStatus("idle");
      }
    }, 350);
    return () => clearTimeout(t);
  }, [username]);

  const submit = async (e) => {
    e.preventDefault();
    if (status !== "available") return;
    if (!email.trim()) {
      toast.error("Enter your email so we can send you the claim link.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/username-reservations", {
        username,
        email: email.trim().toLowerCase(),
      });
      setReserved({ username: data.username, expires_at: data.expires_at });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------- Success state --------------------
  if (reserved) {
    const expiresIn = (() => {
      try {
        const ms = new Date(reserved.expires_at) - new Date();
        const hours = Math.max(1, Math.round(ms / 36e5));
        return `${hours}h`;
      } catch {
        return "48h";
      }
    })();
    return (
      <div
        className="rounded-3xl border-2 border-emerald-300 bg-emerald-50/70 p-6 sm:p-7 shadow-sm"
        data-testid="username-reserve-success"
      >
        <div className="flex items-center gap-2.5 mb-3">
          <div className="h-10 w-10 grid place-items-center rounded-full bg-emerald-500 text-white">
            <CheckCircleIcon size={22} weight="fill" />
          </div>
          <div>
            <div className="font-display font-black text-lg sm:text-xl">
              @{reserved.username} is on hold
            </div>
            <div className="text-xs text-emerald-700 font-semibold tracking-wide uppercase">
              Held for {expiresIn}
            </div>
          </div>
        </div>
        <p className="text-sm text-zinc-700 leading-relaxed">
          We just emailed a one-tap claim link to{" "}
          <span className="font-semibold text-zinc-900">{email}</span>. Open it
          to finish creating your account before the timer runs out — if you
          don't, the handle goes back into the pool.
        </p>
        <div className="mt-4 flex items-center gap-2 text-xs text-zinc-500">
          <EnvelopeSimpleIcon size={14} />
          <span>Tip: check spam if it doesn't appear in 60 seconds.</span>
        </div>
      </div>
    );
  }

  // -------------------- Form --------------------
  const indicator = (() => {
    if (status === "checking")
      return (
        <CircleNotchIcon
          size={18}
          className="animate-spin text-zinc-400"
          data-testid="username-status-checking"
        />
      );
    if (status === "available")
      return (
        <CheckCircleIcon
          size={18}
          weight="fill"
          className="text-emerald-500"
          data-testid="username-status-available"
        />
      );
    if (status === "taken" || status === "invalid")
      return (
        <XCircleIcon
          size={18}
          weight="fill"
          className="text-red-500"
          data-testid="username-status-taken"
        />
      );
    return null;
  })();

  return (
    <form
      onSubmit={submit}
      className="rounded-3xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-white to-amber-50/30 p-6 sm:p-7 shadow-sm"
      data-testid="username-reserve-card"
    >
      <div className="flex items-center gap-1.5 mb-4">
        <SparkleIcon size={14} weight="fill" className="text-amber-500" />
        <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-amber-700">
          Founder handles · open
        </span>
      </div>

      <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight text-zinc-900">
        Reserve your @handle.
      </h2>
      <p className="text-sm text-zinc-600 mt-1.5 leading-relaxed">
        We'll hold it for 48 hours while you decide. No password, no commitment —
        just an email and the username you want.
      </p>

      {/* Username field */}
      <label className="block mt-5">
        <span className="text-xs font-bold tracking-wider uppercase text-zinc-500">
          Username
        </span>
        <div className="mt-1.5 flex items-center rounded-2xl border-2 border-zinc-200 bg-white pl-3 pr-3 focus-within:border-[#1D78AB] transition-colors">
          <span className="text-zinc-400 font-mono">@</span>
          <Input
            value={username}
            onChange={(e) => setUsername(SANITIZE(e.target.value))}
            placeholder="bidqueen99"
            className="border-0 shadow-none focus-visible:ring-0 px-2 font-mono text-base"
            maxLength={20}
            autoComplete="off"
            spellCheck={false}
            data-testid="username-reserve-input"
          />
          <div className="ml-2">{indicator}</div>
        </div>
        {reason && (
          <p
            className={`text-xs mt-1.5 ${
              status === "available" ? "text-emerald-600" : "text-red-500"
            }`}
            data-testid="username-status-reason"
          >
            {reason}
          </p>
        )}
        {status === "available" && (
          <p className="text-xs mt-1.5 text-emerald-600 font-semibold">
            ✓ Available — yours to grab.
          </p>
        )}
      </label>

      {/* Email field */}
      <label className="block mt-4">
        <span className="text-xs font-bold tracking-wider uppercase text-zinc-500">
          Email
        </span>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="you@gmail.com"
          className="mt-1.5 rounded-2xl border-2 border-zinc-200 focus-visible:ring-0 focus:border-[#1D78AB] h-11"
          data-testid="username-reserve-email"
        />
      </label>

      <Button
        type="submit"
        disabled={status !== "available" || submitting || !email.trim()}
        className="mt-5 w-full h-12 rounded-2xl bg-[#1D78AB] hover:bg-[#165d85] disabled:bg-zinc-300 text-white font-semibold text-base"
        data-testid="username-reserve-submit"
      >
        {submitting
          ? "Reserving…"
          : status === "available"
          ? `Reserve @${username || "handle"} for 48h`
          : "Reserve handle"}
      </Button>

      <p className="text-[11px] text-zinc-400 mt-3 leading-relaxed">
        We'll only email the claim link — no marketing, no spam. You can ignore
        it and the handle returns to the pool.
      </p>
    </form>
  );
}
